import { MouseEvent, useEffect, useState } from 'react';
import { styled, alpha } from '@mui/material/styles';
import { Button, ButtonProps, Menu as BaseMenu, MenuProps, MenuItem } from '@mui/material';
import { ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import { CSVLink } from 'react-csv';

import {
    ATTRIBUTION_EXPORT_METHOD_DOWNLOAD,
    ATTRIBUTION_EXPORT_METHOD_EMAIL,
    // ATTRIBUTION_EXPORT_TYPE_CSV,
    ATTRIBUTION_EXPORT_TYPE_PDF,
    AttributionExport,
} from '../../../../types/AttributionExport';

import { useMediaReportingPerformanceCsvExporter } from '../../../../hooks/useReportingPerformanceExport';
import useReportingPerformanceExport from '../../../../hooks/useReportingPerformanceExport';
import { useAttributionPageContext } from '../../../../hooks/useAttributionPage';
import ReportingPerformanceExportDialog from './ReportingPerformanceExportDialog';

const Menu = styled((props: MenuProps) => (
    <BaseMenu
        elevation={4}
        anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
        }}
        transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
        }}
        {...props}
    />
))(({ theme }) => ({
    '& .MuiPaper-root': {
        borderRadius: 6,
        marginTop: 0,
        minWidth: 120,
        color: theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
        '& .MuiMenu-list': {
            padding: '4px 0',
        },
        '& .MuiMenuItem-root': {
            '& .MuiSvgIcon-root': {
                fontSize: 18,
                color: theme.palette.text.secondary,
                marginRight: theme.spacing(1.5),
            },
            '&:active': {
                backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
            },
        },
    },
}));

export default function ReportingPerformanceExportMenuButton(props: ButtonProps) {
    const mediaCsvExporter = useMediaReportingPerformanceCsvExporter();

    const { attributionType, dealer, attributionDate } = useAttributionPageContext();
    const { generateExportFilename } = useReportingPerformanceExport();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const [attributionExport, setAttributionExport] = useState<AttributionExport>({
        exportMethod: ATTRIBUTION_EXPORT_METHOD_DOWNLOAD,
        exportType: ATTRIBUTION_EXPORT_TYPE_PDF,

        dealerId: dealer ? dealer.id : 0,
        attributionDate: attributionDate,
        attributionType: attributionType,
    });
    const [exportDialogOpen, setExportDialogOpen] = useState<boolean>(false);

    const [filename, setFilename] = useState<string>('');

    useEffect(() => {
        const fetchFilename = async () => {
            const name = await generateExportFilename('{id}.csv');
            setFilename(name);
        };

        fetchFilename();
    }, [generateExportFilename]);

    const handleClick = (event: MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const getCsvData = () => {
        let rows = [];

        rows = mediaCsvExporter.getCsvData();

        return rows;
    };

    return (
        <div>
            <Button
                id="reporting-performance-export-menu-button"
                aria-controls={open ? 'reporting-performance-export-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                variant="outlined"
                disableElevation
                onClick={handleClick}
                endIcon={<ArrowDropDownIcon />}
                size="medium"
                {...props}
            >
                Export
            </Button>
            <Menu
                id="reporting-performance-export-menu"
                MenuListProps={{
                    'aria-labelledby': 'reporting-performance-export-menu-button',
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                sx={{ width: 300 }}
            >
                <MenuItem
                    disableRipple
                    onClick={() => {
                        setAttributionExport({
                            ...attributionExport,
                            exportMethod: ATTRIBUTION_EXPORT_METHOD_DOWNLOAD,
                            exportType: ATTRIBUTION_EXPORT_TYPE_PDF,
                        });
                        setExportDialogOpen(true);
                    }}
                >
                    PDF
                </MenuItem>
                <MenuItem disableRipple>
                    <CSVLink
                        data={getCsvData()}
                        filename={filename}
                        style={{
                            display: 'inline-block',
                            width: '100%',
                            textDecoration: 'none',
                        }}
                    >
                        CSV
                    </CSVLink>
                </MenuItem>
                <MenuItem
                    disableRipple
                    onClick={() => {
                        setAttributionExport({
                            ...attributionExport,
                            exportMethod: ATTRIBUTION_EXPORT_METHOD_EMAIL,
                            exportType: ATTRIBUTION_EXPORT_TYPE_PDF,
                        });
                        setExportDialogOpen(true);
                    }}
                >
                    Email
                </MenuItem>
            </Menu>

            <ReportingPerformanceExportDialog
                open={exportDialogOpen}
                onClose={() => {
                    setExportDialogOpen(false);
                }}
                attributionExport={attributionExport}
            />
        </div>
    );
}
